import { useMemo } from 'react';
import { t, Trans } from '@lingui/macro';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import FormSection from '../../form/FormSection';
import { COLUMN_TYPES, columnValueGetter } from '../../data-table/DataTableColumns';
import { INNER_CELL_VALUE_CLASS } from '../../data-table/DataTableConstants';
import { LegacyIcon } from '../../icon';
import {
    AGENT_MODEL_SHORT_DISPLAY_FIELD,
    AGENT_NAME_FIELD,
    AGENT_NETWORK_HOSTNAME_FIELD,
    AGENT_SOFTWARE_VERSION_FIELD,
    AGENT_STATUS_FIELD,
    AGENT_TAGS_DISPLAY_FIELD,
    AGENT_TRANSLATED_TYPE_FIELD,
    AGENT_TYPE_ICON_FIELD,
} from '../agents/AgentConstants';
import { renderStatus, renderVersion } from '../agents/AgentRenderHelper';
import { useColumnStyles } from '../agents/AgentStyles';
import { softwareVersionMapper } from '../agents/AgentVersionHelper';
import ListSelectorField from '../../form/ListSelectorField';
import { SORT_ASC } from '../../../utilities/FormUtilities';
import { DEFAULT_INVALID_VALUE } from '../../../utilities/FormattingUtilities';
import { CERTIFICATE_USAGES } from './CertificateConstants';

const initialSort = [{ property: AGENT_NAME_FIELD, direction: SORT_ASC }];

function mapAssociatedCertificates(certificateDetails, certificateUsage) {
    if (!Array.isArray(certificateDetails)) {
        return [];
    }

    return certificateDetails.filter((cert) => cert.certificateUsage === certificateUsage).map((cert) => cert.id);
}

export function buildAgentColumns(classes, certificateUsage) {
    return [
        {
            type: COLUMN_TYPES.ICON,
            title: t`Type`,
            width: 74,
            mapping: AGENT_TYPE_ICON_FIELD,
            sortable: true,
            csvFormatter: (value, row) => row[AGENT_TRANSLATED_TYPE_FIELD],
            icons: [
                {
                    getIcon: (row) => <LegacyIcon icon={row[AGENT_TYPE_ICON_FIELD]} />,
                    getTooltip: (row) => row[AGENT_TRANSLATED_TYPE_FIELD],
                },
            ],
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Name`,
            removable: false,
            sortable: true,
            mapping: AGENT_NAME_FIELD,
            width: 200,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Model`,
            sortable: true,
            mapping: AGENT_MODEL_SHORT_DISPLAY_FIELD,
            width: 85,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Certificates`,
            sortable: true,
            mapping: 'certificateDetails',
            mapper: (row, column) => mapAssociatedCertificates(row[column.mapping], certificateUsage),
            width: 120,
            align: 'center',
            renderer: (row, column) => {
                const certs = columnValueGetter(row, column);
                const hasCert = Boolean(certs) && certs !== DEFAULT_INVALID_VALUE;
                return (
                    <div className={INNER_CELL_VALUE_CLASS}>
                        {hasCert ? (
                            <VerifiedUserIcon
                                fontSize="small"
                                titleAccess={t`Certificates associated`}
                                style={{ paddingLeft: 4, fontSize: 18, cursor: 'default' }}
                            />
                        ) : (
                            <span>{DEFAULT_INVALID_VALUE}</span>
                        )}
                    </div>
                );
            },
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Status`,
            sortable: true,
            mapping: AGENT_STATUS_FIELD,
            width: 105,
            className: classes.root,
            renderer: renderStatus,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Tags`,
            sortable: true,
            mapping: AGENT_TAGS_DISPLAY_FIELD,
            width: 150,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Version`,
            sortable: true,
            mapping: AGENT_SOFTWARE_VERSION_FIELD,
            className: classes.root,
            mapper: softwareVersionMapper,
            renderer: renderVersion,
            width: 125,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Hostname`,
            sortable: true,
            mapping: AGENT_NETWORK_HOSTNAME_FIELD,
            flex: 1,
        },
    ];
}

const selectionDataSource = {
    totalProperty: 'count',
    dataProperty: 'data',
    params: {
        searchFields: [
            AGENT_NAME_FIELD,
            AGENT_STATUS_FIELD,
            AGENT_TAGS_DISPLAY_FIELD,
            AGENT_MODEL_SHORT_DISPLAY_FIELD,
            AGENT_NETWORK_HOSTNAME_FIELD,
        ],
    },
    initialSort: [{ property: AGENT_NAME_FIELD, direction: SORT_ASC }],
};

const selectedDataSource = {
    ...selectionDataSource,
    url: '/query/admin/agents/findSelected',
};

const idsDataSource = {
    params: selectionDataSource.params,
    url: '/query/admin/agents/selectionUUIDs',
};

export default function CertificateAgents({ formData, setAgentSelectionIsDirty, certificateUsage }) {
    const classes = useColumnStyles();
    const gridColumns = buildAgentColumns(classes, certificateUsage);

    const availableDataSource = useMemo(() => {
        const params = { ...selectionDataSource.params };
        if (certificateUsage !== CERTIFICATE_USAGES.voip) {
            params.nextGen = true;
        }
        return {
            ...selectionDataSource,
            params,
            url: '/query/admin/agents/findAvailable',
        };
    }, [certificateUsage]);

    return (
        <FormSection title={<Trans>Associated nPoints</Trans>} addSpacer>
            <ListSelectorField
                isMultiRemove={false}
                gridColumns={gridColumns}
                selectedGridColumns={gridColumns}
                dbSelected={formData?.nPoints?.ids}
                sorters={initialSort}
                selectedDataSource={selectedDataSource}
                availableDataSource={availableDataSource}
                idsDataSource={idsDataSource}
                fieldName={'nPoints.ids'}
                addButtonText={<Trans>Add nPoints</Trans>}
                removeButtonText={<Trans>Remove nPoints</Trans>}
                setSelectionIsDirty={setAgentSelectionIsDirty}
                dialogMaxWidth={'lg'}
                dialogFullWidth
            />
        </FormSection>
    );
}
